import { useState, useRef, useEffect } from "react";
import { lsGet, lsSet } from "../utils/storage.js";
import { generateId, now } from "../utils/slug.js";
import { analyzeSentiment } from "../utils/sentiment.js";
import { sendChat } from "../utils/api.js";
import { sendDeepDive, getChatHistory, submitFeedback, getSuggestions } from "../services/chat.js";
import { Ava } from "../atoms/Ava.jsx";
// v1.27.0: Badge import removed — was only used for the party badge in
// the chat header, which is now gone.
import { XpToast, TriviaPlayUnlockedModal } from "../molecules/ChatOverlays/index.jsx";
import { ChatMessageBubble } from "../molecules/ChatMessageBubble/index.jsx";

// ── Chat ───────────────────────────────────────────────────────────────────────
/** Strip the "Tell me more →" hint from the display text while keeping context. */
function stripTellMeMore(text) {
  return text.replace(/\s*Tell me more\s*[→>]\s*$/i, "").trimEnd();
}

/** Returns true if an assistant message ends with the Tell me more hint. */
function hasTellMeMore(text) {
  return /Tell me more\s*[→>]\s*$/i.test(text.trim());
}

/** Persist chat messages (including sentiment) to localStorage. */
function saveChats(voterPhone, personaId, messages) {
  if (!voterPhone) return;
  const chats = lsGet("qm_chats", {});
  if (!chats[voterPhone]) chats[voterPhone] = {};
  chats[voterPhone][personaId] = messages.map(m => ({
    role: m.role, text: m.content, ts: m.timestamp, sentiment: m.sentiment,
  }));
  lsSet("qm_chats", chats);
}

/**
 * RWD-7/RWD-8: Chat now takes per-voter XP state instead of per-persona civics.
 * `xpState` comes from /api/voters/me/xp and is the single source of truth for
 * the voter's balance, cumulative earned total, and available trivia plays.
 * `onXpEarned` is called with the numeric xpEarned from each chat response so
 * the parent hook can update the cached state optimistically.
 * `onPlayTrivia(persona)` still opens the trivia modal — the server RPC gates
 * the actual play consumption.
 */
export function Chat({persona, onBack, onFeedback, onUpdateConv, xpState, onXpEarned, onPlayTrivia, voterPhone, voterEmail, isAdmin}) {
  // BUG-H3: Admin preview mode — use synthetic phone so chat works without a voter session
  // EMAIL-11: For email-only voters, use email as the LOCAL identifier (localStorage keying).
  // API identity comes from the JWT — phone is only sent for admin-preview bypass.
  const effectivePhone = voterPhone || (isAdmin ? "admin-preview" : "");
  const effectiveIdentifier = effectivePhone || voterEmail || "";
  const [msgs, setMsgs] = useState(() => {
    if (!effectiveIdentifier) return [];
    try {
      const chats = lsGet("qm_chats", {});
      const saved = chats[effectiveIdentifier]?.[persona.id];
      if (saved?.length) return saved.map(m => ({
        id: generateId(), role: m.role, content: m.text || m.content,
        timestamp: m.ts,
        sentiment: m.sentiment ?? (m.role === "user" ? analyzeSentiment(m.text || m.content) : undefined),
        feedback: null, feedbackLocked: false,
      }));
    } catch {}
    return [];
  });

  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyOffset, setHistoryOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const HISTORY_LIMIT = 10;

  // CHAT-1: Dynamic suggestions
  const [dynamicSuggestions, setDynamicSuggestions] = useState(null);

  const fetchSuggestions = () => {
    if (!effectiveIdentifier) return;
    getSuggestions(persona.id, effectivePhone)
      .then(data => { if (data?.suggestions?.length) setDynamicSuggestions(data.suggestions); })
      .catch(() => {});
  };

  // Load most-recent HISTORY_LIMIT messages on mount
  useEffect(() => {
    if (!effectiveIdentifier) return;
    const lsCache = lsGet("qm_chats", {})[effectiveIdentifier]?.[persona.id] ?? [];
    getChatHistory(persona.id, effectivePhone, HISTORY_LIMIT, 0)
      .then(result => {
        const rows = result?.messages ?? result ?? [];
        const total = result?.total ?? rows.length;
        setHistoryTotal(total);
        setHistoryOffset(Math.max(0, total - HISTORY_LIMIT));
        if (rows?.length) {
          setMsgs(rows.map(r => {
            const cached = lsCache.find(m => m.ts === r.created_at && m.role === r.role);
            return {
              id: r.id ?? generateId(),
              role: r.role,
              content: r.content,
              timestamp: r.created_at,
              sentiment: cached?.sentiment ?? (r.role === "user" ? analyzeSentiment(r.content) : undefined),
              // CHAT-2: Load persisted feedback state
              feedback: r.feedback ?? null,
              feedbackLocked: !!r.feedback,
            };
          }));
          setTimeout(() => bot.current?.scrollIntoView({ behavior: "instant" }), 80);
        }
      })
      .catch(() => {});

    // CHAT-1: Fetch suggestions on mount
    fetchSuggestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persona.id, effectiveIdentifier]);

  const loadEarlierMessages = async () => {
    if (loadingMore || historyOffset <= 0) return;
    setLoadingMore(true);
    try {
      // historyOffset = number of messages older than what's currently displayed
      // The API orders DESC so the db offset is how many messages we've already loaded
      const dbOffset = historyTotal - historyOffset;
      const newOffset = Math.max(0, historyOffset - HISTORY_LIMIT);
      const batchLimit = historyOffset - newOffset;
      const lsCache = lsGet("qm_chats", {})[effectiveIdentifier]?.[persona.id] ?? [];
      const result = await getChatHistory(persona.id, effectivePhone, batchLimit, dbOffset);
      const rows = result?.messages ?? result ?? [];
      if (rows.length) {
        const older = rows.map(r => {
          const cached = lsCache.find(m => m.ts === r.created_at && m.role === r.role);
          return {
            id: r.id ?? generateId(),
            role: r.role,
            content: r.content,
            timestamp: r.created_at,
            sentiment: cached?.sentiment ?? (r.role === "user" ? analyzeSentiment(r.content) : undefined),
            feedback: r.feedback ?? null,
            feedbackLocked: !!r.feedback,
          };
        });
        setMsgs(prev => [...older, ...prev]);
        setHistoryOffset(newOffset);
      }
    } catch { /* ignore */ }
    finally { setLoadingMore(false); }
  };

  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [deepDiveBusy, setDeepDiveBusy] = useState(null);
  const [err, setErr] = useState(null);
  const [cid] = useState(generateId);
  const [xpToast, setXpToast] = useState(null);
  // Auto-dismiss duplicate/suggested toasts after 4 seconds
  useEffect(() => {
    if (xpToast?.duplicate || xpToast?.suggested) {
      const t = setTimeout(() => setXpToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [xpToast]);
  const [showTriviaModal, setShowTriviaModal] = useState(false);

  // v1.28.3 — AI disclaimer auto-hide.
  // Before: LEGAL-4 disclaimer banner ("Responses are AI-generated…") was
  // rendered unconditionally and stayed visible for the whole session.
  // Users flagged it as clutter after the first question.
  //
  // New rule: show briefly on every chat open (first visit AND returning
  // visits — per-persona state isn't persisted across mounts), then:
  //   a) auto-fade after ~4s, OR
  //   b) hide immediately when the user sends their first message.
  // Whichever comes first. The disclaimer link still lives on the
  // landing page (/disclaimer) for anyone who wants to revisit it.
  const [showAiDisclaimer, setShowAiDisclaimer] = useState(true);
  useEffect(() => {
    if (!showAiDisclaimer) return;
    const t = setTimeout(() => setShowAiDisclaimer(false), 4000);
    return () => clearTimeout(t);
  }, [showAiDisclaimer]);

  const bot = useRef(null);
  const latestAssistantRef = useRef(null); // anchor for scrolling to top of newest AI reply
  const [atBottom, setAtBottom] = useState(true); // tracks whether the bottom anchor is visible

  // RWD-7/RWD-8: XP state is per-voter, not per-persona. Normalise the prop
  // so the component renders even when the parent is still loading.
  const xpPerPlay     = xpState?.xpPerTriviaPlay ?? 10;
  const totalEarned   = xpState?.totalEarned ?? 0;
  const xpBalance     = xpState?.balance ?? 0;
  const playsAvailable = xpState?.triviaPlaysAvailable ?? 0;
  // Progress toward next trivia play unlock (0 → xpPerPlay as totalEarned accrues)
  const xpInCycle      = totalEarned % xpPerPlay;
  const xpToNextPlay   = xpPerPlay - xpInCycle;
  const unlockProgress = xpInCycle / xpPerPlay;

  // Trigger trivia-play-unlocked modal only when an XP earn event actually
  // crosses a multiple of xpPerPlay. Re-mounting with the same total should
  // not re-fire the modal.
  const prevTotalEarned = useRef(totalEarned);
  const xpJustEarned = useRef(false);
  useEffect(() => {
    if (!xpJustEarned.current) {
      prevTotalEarned.current = totalEarned;
      return;
    }
    xpJustEarned.current = false;
    const prev = prevTotalEarned.current;
    const prevPlays = Math.floor(prev / xpPerPlay);
    const curPlays = Math.floor(totalEarned / xpPerPlay);
    if (curPlays > prevPlays && playsAvailable > 0) {
      setShowTriviaModal(true);
    }
    prevTotalEarned.current = totalEarned;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalEarned]);

  // Track newly arrived message IDs for fade-in animation
  const [newMsgIds, setNewMsgIds] = useState(new Set());
  const markNew = (id) => {
    setNewMsgIds(prev => new Set(prev).add(id));
    setTimeout(() => setNewMsgIds(prev => { const n = new Set(prev); n.delete(id); return n; }), 400);
  };

  const pc = persona.accentColor;

  useEffect(() => {
    setTimeout(() => bot.current?.scrollIntoView({ behavior: "instant" }), 80);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track whether the bottom anchor is visible so we can show a "↓ More" indicator
  useEffect(() => {
    const el = bot.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const obs = new IntersectionObserver(([entry]) => setAtBottom(entry.isIntersecting), { threshold: 0 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const scroll = () => setTimeout(() => bot.current?.scrollIntoView({ behavior: "smooth" }), 50);
  // Scroll to the TOP of the latest assistant response so the user reads from the beginning
  const scrollToResponse = () => setTimeout(() => latestAssistantRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);

  const send = async (txt, opts = {}) => {
    if (!txt.trim() || busy) return;
    setErr(null);
    // v1.28.3 — hide the AI-generated disclaimer as soon as the user
    // actually sends something. The 4s timer above handles the passive
    // case (user reads the banner and it fades); this handles the
    // active case (user skips straight to typing).
    setShowAiDisclaimer(false);
    const msgSentiment = analyzeSentiment(txt);
    const uMsg = { id: generateId(), role: "user", content: txt, sentiment: msgSentiment, timestamp: now(), feedback: null, feedbackLocked: false };
    const next = [...msgs, uMsg];
    setMsgs(next); setInput(""); setBusy(true); scroll();
    try {
      // RWD-7/RWD-8: response field is xpEarned (was cpEarned pre-RWD-8).
      const { reply, xpEarned = 0, cached = false, userMessageId, assistantMessageId, kbReferenceCount = 0, isDuplicate = false, hasVerifiedKb: _hasVerifiedKb = true, isSuggested: _wasSuggested = false, sources = [] } = await sendChat(
        persona.id,
        effectivePhone,
        txt,
        msgSentiment,
        opts.isSuggested
      );
      // BUG-H4: Use real DB UUIDs so feedback submission uses the correct ID
      if (userMessageId) uMsg.id = userMessageId;
      const aMsg = { id: assistantMessageId || generateId(), role: "assistant", content: reply, timestamp: now(), feedback: null, feedbackLocked: false, kbReferenceCount, hasVerifiedKb: _hasVerifiedKb, sources };
      const final = [...next.map(m => m === uMsg ? { ...m, id: uMsg.id, isDuplicate } : m), aMsg];
      setMsgs(final);
      markNew(aMsg.id);
      onUpdateConv(persona.id, { id: cid, messages: final });
      scrollToResponse();
      saveChats(effectiveIdentifier, persona.id, final);
      if (_wasSuggested || opts.isSuggested) {
        setXpToast({ xp: 0, key: generateId(), suggested: true });
      } else if (isDuplicate && cached) {
        setXpToast({ xp: 0, key: generateId(), duplicate: true, cached: true });
      } else if (isDuplicate) {
        setXpToast({ xp: 0, key: generateId(), duplicate: true });
      } else if (xpEarned > 0) {
        setXpToast({ xp: xpEarned, key: generateId() });
        xpJustEarned.current = true;
        onXpEarned?.(xpEarned);
      }
      // CHAT-1: Refresh suggestions after each exchange
      fetchSuggestions();
    } catch { setErr("Failed to respond. Please try again."); }
    finally { setBusy(false); }
  };

  const deepDive = async (assistantMsg) => {
    const idx = msgs.findIndex(m => m.id === assistantMsg.id);
    const userMsg = idx > 0 ? [...msgs].slice(0, idx).reverse().find(m => m.role === "user") : null;
    if (!userMsg || deepDiveBusy) return;
    setDeepDiveBusy(assistantMsg.id);
    setErr(null);
    try {
      const { reply, sources = [] } = await sendDeepDive(
        persona.id,
        effectivePhone,
        { question: userMsg.content, answer: assistantMsg.content }
      );
      const deepMsg = {
        id: generateId(), role: "assistant", content: reply,
        timestamp: now(), feedback: null, feedbackLocked: false, isDeepDive: true, sources,
      };
      const final = [...msgs, deepMsg];
      setMsgs(final);
      markNew(deepMsg.id);
      onUpdateConv(persona.id, { id: generateId(), messages: final });
      scrollToResponse();
      saveChats(effectiveIdentifier, persona.id, final);
    } catch { setErr("Deep dive failed. Please try again."); }
    finally { setDeepDiveBusy(null); }
  };

  // CHAT-2: Persist feedback to API with locking
  const fb = async (mid, type, flagReason) => {
    const msg = msgs.find(m => m.id === mid);
    if (!msg || msg.feedbackLocked) return;

    // Optimistic lock
    setMsgs(p => p.map(m => m.id === mid ? { ...m, feedback: type, feedbackLocked: true } : m));

    try {
      const result = await submitFeedback(mid, type, flagReason, effectiveIdentifier, persona.id);
      // RWD-7/RWD-8: feedback award is xpEarned, not cpEarned.
      if (result.xpEarned > 0) {
        setXpToast({ xp: result.xpEarned, key: generateId() });
        xpJustEarned.current = true;
        onXpEarned?.(result.xpEarned);
      }
    } catch {
      // Revert on failure
      setMsgs(p => p.map(m => m.id === mid ? { ...m, feedback: null, feedbackLocked: false } : m));
    }

    // Also update local state for backward compat
    onFeedback(persona.id, mid, type);
  };

  // Resolved suggestions: dynamic if available, otherwise static
  const suggestions = dynamicSuggestions ?? persona.suggestedPrompts;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",width:"100%",maxWidth:720,margin:"0 auto",overflow:"hidden"}}>
      {/* BUG-H3: Admin preview banner */}
      {isAdmin && (
        <div style={{padding:"6px 16px",background:"#f59e0b18",borderBottom:"1px solid #f59e0b30",fontSize:11,color:"#f59e0b",textAlign:"center",fontWeight:600,flexShrink:0}}>
          🔧 Admin Preview Mode — XP scoring disabled
        </div>
      )}
      {/* Header */}
      <div style={{padding:"10px 16px",borderBottom:"1px solid #1e2d45",display:"flex",alignItems:"center",gap:11,background:"#090e1a",flexShrink:0}}>
        <button onClick={onBack} style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",fontSize:20,padding:"2px 5px",lineHeight:1,flexShrink:0}}>←</button>
        <Ava ini={persona.avatar} color={pc} size={36} photoUrl={persona.photoUrl}/>
        <div style={{flex:1,minWidth:0}}>
          <div style={{color:"#f1f5f9",fontWeight:800,fontSize:14,fontFamily:"Georgia,serif",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{persona.name}</div>
          <div style={{color:"#64748b",fontSize:11,fontFamily:"monospace",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{((import.meta.env.VITE_APP_URL ?? "").replace(/^https?:\/\//, "").replace(/\/$/, "") || (typeof window !== "undefined" ? window.location.origin.replace(/^https?:\/\//, "") : "questionme.io"))}/{persona.slug}</div>
        </div>

        {/* RWD-7/RWD-8: trivia-unlock progress pill + play button.
            The pill shows `xpInCycle` (XP earned in the current unlock cycle,
            0–xpPerPlay) and the fill tracks the same value. This keeps the
            number and the fill bar consistent — xpBalance (spendable) can
            decrease via Reward Shop spending while the unlock progress never
            regresses. Balance is shown separately in the Reward Shop view. */}
        <div style={{display:"flex",alignItems:"center",gap:7,flexShrink:0}}>
          <div style={{display:"flex",alignItems:"center",gap:5,background:"#0d1724",border:"1px solid #1e2d45",borderRadius:20,padding:"4px 10px",position:"relative",overflow:"hidden"}}>
            <div style={{position:"absolute",inset:0,background:`linear-gradient(90deg,${pc}18,transparent)`,width:`${Math.round(unlockProgress*100)}%`,transition:"width .6s ease"}}/>
            <span style={{color:"#64748b",fontSize:10,position:"relative"}}>XP</span>
            <span style={{color:pc,fontWeight:800,fontSize:13,fontFamily:"Georgia,serif",position:"relative"}}>{xpInCycle}</span>
          </div>
          {playsAvailable > 0 && (
            <button onClick={() => onPlayTrivia(persona)} style={{display:"flex",alignItems:"center",gap:4,background:"#f59e0b18",border:"1px solid #f59e0b40",borderRadius:20,padding:"4px 10px",cursor:"pointer"}}>
              <span style={{fontSize:12}}>🎫</span>
              <span style={{color:"#f59e0b",fontWeight:800,fontSize:12}}>{playsAvailable}</span>
            </button>
          )}
          {/* v1.27.0: party badge removed from the chat header. The accent
              colour on the avatar + typing indicator is enough visual
              identity; no need for the party label. */}
        </div>
      </div>

      {/* XP-to-next-play progress bar */}
      <div style={{height:2,background:"#0d1724",flexShrink:0}}>
        <div style={{height:"100%",background:`linear-gradient(90deg,${pc},#8b5cf6)`,width:`${Math.round(unlockProgress*100)}%`,transition:"width .6s ease"}}/>
      </div>

      {/* LEGAL-4: AI disclaimer banner.
          v1.28.3 — auto-hides after 4s or on first message send (see
          showAiDisclaimer). We use opacity + height for a smooth fade
          rather than unmounting so the layout doesn't jump. `aria-hidden`
          flips once the banner goes invisible so screen readers don't
          announce stale text. */}
      <div
        aria-hidden={!showAiDisclaimer}
        data-testid="ai-disclaimer-banner"
        style={{
          padding: showAiDisclaimer ? "6px 16px" : "0 16px",
          background:"#0d172488",
          borderBottom: showAiDisclaimer ? "1px solid #1e2d4540" : "none",
          fontSize:11,color:"#64748b",textAlign:"center",flexShrink:0,lineHeight:1.5,
          opacity: showAiDisclaimer ? 1 : 0,
          maxHeight: showAiDisclaimer ? 40 : 0,
          overflow: "hidden",
          transition: "opacity .3s ease, max-height .3s ease, padding .3s ease",
          pointerEvents: showAiDisclaimer ? "auto" : "none",
        }}
      >
        <span style={{marginRight:4}}>🤖</span>
        Responses are <strong style={{color:"#94a3b8"}}>AI-generated</strong> and may not reflect the candidate's actual views.{" "}
        <a href={`${(import.meta.env.VITE_LANDING_URL ?? "").replace(/\/$/, "")}/disclaimer`} target="_blank" rel="noopener noreferrer" style={{color:"#3b82f6",textDecoration:"underline"}}>Learn more</a>
      </div>

      {/* Messages */}
      <div className="qm-scrollable qm-chat-messages" style={{flex:1,minHeight:0,padding:"18px 16px",display:"flex",flexDirection:"column",gap:14}}>
        {historyOffset > 0 && (
          <button onClick={loadEarlierMessages} disabled={loadingMore}
            style={{alignSelf:"center",padding:"7px 16px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:20,color:"#64748b",fontSize:12,cursor:loadingMore?"not-allowed":"pointer",fontFamily:"inherit",marginBottom:8}}>
            {loadingMore ? "⟳ Loading…" : `↑ Load earlier messages (${historyOffset} more)`}
          </button>
        )}
        {msgs.length === 0 && (
          <div style={{textAlign:"center",paddingTop:24}}>
            <Ava ini={persona.avatar} color={pc} size={68} photoUrl={persona.photoUrl}/>
            <h2 style={{color:"#f1f5f9",marginTop:12,fontFamily:"Georgia,serif",fontSize:19}}>{persona.name}</h2>
            <p style={{color:"#64748b",maxWidth:320,margin:"5px auto 10px",fontSize:13,lineHeight:1.6,fontStyle:"italic"}}>"{persona.tagline}"</p>
            <div style={{display:"inline-flex",alignItems:"center",gap:6,padding:"6px 14px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:20,marginBottom:20,fontSize:11,color:"#475569"}}>
              <span style={{color:pc,fontWeight:700}}>XP</span> Earn XP per question · <span style={{color:"#f59e0b"}}>🎫</span> {xpPerPlay} XP = 1 Trivia Play
            </div>
            <div style={{display:"flex",flexDirection:"column",gap:7,maxWidth:400,margin:"0 auto",textAlign:"left"}}>
              <div style={{fontSize:10,fontWeight:700,letterSpacing:"0.1em",color:"#475569",textTransform:"uppercase",marginBottom:2,textAlign:"center"}}>Suggested Questions</div>
              {suggestions?.map(q => (
                <button key={q} onClick={() => send(q, { isSuggested: true })} style={{padding:"10px 14px",background:"#0d1724",border:`1px solid ${pc}30`,borderRadius:9,color:"#cbd5e1",fontSize:13,cursor:"pointer",textAlign:"left",fontFamily:"inherit",lineHeight:1.4}}>{q}</button>
              ))}
            </div>
          </div>
        )}

        {msgs.map((m, mi) => {
          const isLastAssistant = m.role === "assistant" && msgs.slice(mi + 1).every(x => x.role !== "assistant");
          // CHAT-3: Only show "Tell me more" if persona has KB entries
          // Show "Tell me more" when the backend appended the hint — the backend already
          // controls whether deep-dive is worthwhile; hiding the button on kbCount===0
          // breaks e2e tests where seed personas have no KB entries yet.
          // CHAT-9: Only show "Tell me more" if persona has verified KB entries with source URLs
          const showTellMore = m.role === "assistant" && !m.isDeepDive && hasTellMeMore(m.content) && isLastAssistant && !busy && !deepDiveBusy && (m.kbReferenceCount ?? 0) > 0 && (m.hasVerifiedKb !== false);
          const displayText = m.role === "assistant" && !m.isDeepDive ? stripTellMeMore(m.content) : m.content;
          return (
            <div key={m.id} ref={isLastAssistant ? latestAssistantRef : null}>
              <ChatMessageBubble m={m} persona={persona} pc={pc}
                deepDiveBusy={deepDiveBusy} showTellMore={showTellMore} displayText={displayText}
                isNew={newMsgIds.has(m.id)}
                onDeepDive={deepDive} onFeedback={fb} />
            </div>
          );
        })}

        {busy && (
          <div style={{display:"flex",gap:8,alignItems:"flex-end"}}>
            <Ava ini={persona.avatar} color={pc} size={30} photoUrl={persona.photoUrl}/>
            <div style={{padding:"11px 15px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:"16px 16px 16px 4px",display:"flex",gap:4,alignItems:"center"}}>
              {[0,1,2].map(i => <span key={i} style={{width:6,height:6,borderRadius:"50%",background:pc,display:"inline-block",animation:`bop 1.2s ease-in-out ${i*.2}s infinite`}}/>)}
            </div>
          </div>
        )}
        {err && <div style={{textAlign:"center",color:"#ef4444",fontSize:13,padding:"7px 14px",background:"#ef444411",borderRadius:7,border:"1px solid #ef444430"}}>{err}</div>}
        <div ref={bot}/>
        {/* ↓ More indicator — shown when the bottom of the conversation is out of view */}
        {!atBottom && !busy && msgs.length > 0 && (
          <div style={{position:"sticky",bottom:8,display:"flex",justifyContent:"center",pointerEvents:"none"}}>
            <button onClick={scroll} style={{pointerEvents:"auto",padding:"5px 14px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:20,color:"#64748b",fontSize:12,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",gap:5,boxShadow:"0 2px 8px #00000060"}}>
              ↓ More
            </button>
          </div>
        )}
      </div>

      {/* Quick chips — uses dynamic suggestions */}
      {msgs.length > 0 && !busy && suggestions?.length > 0 && (
        <div style={{padding:"7px 16px 4px",display:"flex",gap:5,overflowX:"auto",flexShrink:0,WebkitOverflowScrolling:"touch",scrollbarWidth:"none",msOverflowStyle:"none"}}>
          {suggestions.slice(0,3).map(q => (
            <button key={q} onClick={() => send(q, { isSuggested: true })} style={{padding:"5px 11px",background:"#0d1724",border:`1px solid ${pc}25`,borderRadius:20,color:"#64748b",fontSize:11,cursor:"pointer",fontFamily:"inherit",whiteSpace:"nowrap",flexShrink:0}}>{q}</button>
          ))}
        </div>
      )}

      {/* Input */}
      <div className="qm-chat-input" style={{padding:"9px 16px",paddingBottom:"max(20px, env(safe-area-inset-bottom, 20px))",background:"#090e1a",flexShrink:0}}>
        {playsAvailable > 0 && (
          <div style={{marginBottom:8}}>
            <button onClick={() => onPlayTrivia(persona)} style={{width:"100%",padding:"9px 14px",background:"#f59e0b12",border:"1px solid #f59e0b35",borderRadius:10,color:"#f59e0b",fontSize:12,fontWeight:700,cursor:"pointer",fontFamily:"inherit",display:"flex",alignItems:"center",justifyContent:"center",gap:7}}>
              🎫 You have {playsAvailable} Trivia Play{playsAvailable > 1 ? "s" : ""} — Play Now
            </button>
          </div>
        )}
        <div style={{display:"flex",gap:8}}>
          <input value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => e.key === "Enter" && !e.shiftKey && send(input)}
            placeholder={`Ask ${persona.name.split(" ")[1] || persona.name.split(" ")[0]}… (+XP for good questions)`}
            style={{flex:1,padding:"12px 14px",background:"#0d1724",border:"1px solid #1e2d45",borderRadius:11,color:"#e2e8f0",fontSize:14,fontFamily:"inherit",outline:"none",minWidth:0}}/>
          <button onClick={() => send(input)} disabled={!input.trim() || busy}
            style={{padding:"12px 16px",background:input.trim()&&!busy?`linear-gradient(135deg,${pc},${pc}bb)`:"#1e2d45",color:input.trim()&&!busy?"#fff":"#475569",border:"none",borderRadius:11,fontSize:17,cursor:input.trim()&&!busy?"pointer":"not-allowed",flexShrink:0}}>↑</button>
        </div>
        <div style={{marginTop:7,display:"flex",alignItems:"center",justifyContent:"space-between",fontSize:10,color:"#334155"}}>
          <span>Quality questions earn more XP (1–3 per exchange)</span>
          <span>{xpToNextPlay} XP to next 🎫</span>
        </div>
      </div>

      {xpToast && (xpToast.suggested
        ? <div key={xpToast.key} style={{position:"fixed",top:70,left:"50%",transform:"translateX(-50%)",background:"#1e2d45",color:"#94a3b8",padding:"10px 18px",borderRadius:10,fontSize:13,fontWeight:600,zIndex:9999,border:"1px solid #2d3f5a",textAlign:"center",maxWidth:320,animation:"fadeInUp .3s ease"}}>
            Suggested questions don't earn XP — type your own to earn XP!
          </div>
        : xpToast.duplicate && xpToast.cached
        ? <div key={xpToast.key} style={{position:"fixed",top:70,left:"50%",transform:"translateX(-50%)",background:"#1e2d45",color:"#94a3b8",padding:"10px 18px",borderRadius:10,fontSize:13,fontWeight:600,zIndex:9999,border:"1px solid #2d3f5a",textAlign:"center",maxWidth:320,animation:"fadeInUp .3s ease"}}>
            ⚡ Cached answer — no reward for duplicate questions
          </div>
        : xpToast.duplicate
        ? <div key={xpToast.key} style={{position:"fixed",top:70,left:"50%",transform:"translateX(-50%)",background:"#1e2d45",color:"#94a3b8",padding:"10px 18px",borderRadius:10,fontSize:13,fontWeight:600,zIndex:9999,border:"1px solid #2d3f5a",textAlign:"center",maxWidth:320,animation:"fadeInUp .3s ease"}}>
            You've asked a similar question before — try a different topic to earn XP!
          </div>
        : <XpToast key={xpToast.key} xp={xpToast.xp} onDone={() => setXpToast(null)}/>
      )}

      {showTriviaModal && (
        <TriviaPlayUnlockedModal
          persona={persona}
          xpPerPlay={xpPerPlay}
          onPlayNow={() => { setShowTriviaModal(false); onPlayTrivia(persona); }}
          onLater={() => setShowTriviaModal(false)}
        />
      )}
    </div>
  );
}

export default Chat;
